import { useContext } from 'preact/hooks';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import WinnerIcon from '../../shared-components/icons/WinnerIcon';

import styleSheet from '../poll.module.scss';
import { PollContext } from '../../../potg/potg.component';
import { useTranslation } from 'react-i18next';

const styles = styleSheet.locals || {};

/**
 * Component for a single poll option
 *
 * @param {object} props poll option props
 * @param {object} props.option poll option data
 * @param {boolean} props.showingResults poll is showing results
 * @param {object} props.transitionStyles transition styles
 * @param {string} props.transitionState transition state
 * @param {number} props.optionsVisibleDelay delay for visibility transition
 * @param {boolean} props.isSelected if option is selected by user
 * @param {boolean} props.isAnswered if option has been chosen as an answer by user
 * @param {object} props.optionTextRef option text node reference
 * @returns {Function} <PollTextOption />
 */
function PollTextOption({
    option,
    showingResults = false,
    transitionStyles,
    transitionState,
    optionsVisibleDelay,
    optionTextRef
}) {
    const { t } = useTranslation();
    const { poll, showVotesCounter, isPreview } = useContext(PollContext);
    const editableAttr = isPreview ? { contentEditable: true } : {};
    const barTransitionStyles = {
        entered: { maxWidth: '100%' }
    };
    const winnerTransitionStyles = {
        entered: { opacity: 1, transform: 'scale(1)' }
    };

    return (
        <div
            className={classNames(
                styles.pollOptionText,
                styles.pollOptionTextOnly
            )}
        >
            {showingResults && (
                <div
                    className={styles.pollOptionBar}
                    style={{
                        width: `${option.percentage}%`,
                        ...barTransitionStyles[transitionState],
                        transitionDelay: `${optionsVisibleDelay}ms`
                    }}
                />
            )}
            <span
                part="potg-option-label"
                className={styles.pollOptionLabel}
                {...editableAttr}
                ref={optionTextRef}
            >
                {option.text}
            </span>
            {showingResults && (
                <div className={styles.pollOptionRight}>
                    <div
                        className={styles.pollOptionData}
                        style={{
                            ...transitionStyles[transitionState],
                            transitionDelay: `${optionsVisibleDelay}ms`
                        }}
                    >
                        <div className={styles.pollOptionPercentage}>
                            {option.percentage}%
                        </div>
                        {showVotesCounter && (
                            <div className={styles.pollOptionCounter}>
                                {option.times_selected} {t('common.votes')}
                            </div>
                        )}
                    </div>
                    {option.winner && poll?.state === 'closed' && (
                        <WinnerIcon
                            className={styles.pollOptionWinnerIcon}
                            style={{
                                ...winnerTransitionStyles[transitionState]
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

PollTextOption.propTypes = {
    option: PropTypes.object.isRequired,
    showingResults: PropTypes.bool,
    transitionStyles: PropTypes.object,
    transitionState: PropTypes.string,
    optionsVisibleDelay: PropTypes.number,
    isSelected: PropTypes.bool,
    isAnswered: PropTypes.bool,
    optionTextRef: PropTypes.object
};

export default PollTextOption;
